import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { EntityState, createEntityAdapter } from '@ngrx/entity';
import { defaultPaginationState, PaginationState } from '@shared/models/pagination-state';
import { User } from '@shared/models/user';
import { LoadingState } from '@shared/types/loading-state';
import { loadUsers, loadUsersFailure, loadUsersSuccess } from '../actions/load-users.actions';
import { UserFilters } from '@shared/models/user-filters';
import { Sorting } from '@shared/models/sorting';
import { loadUser, loadUserFailure, loadUserSuccess } from '../actions/load-user.actions';
import { createUser, createUserFailure, createUserSuccess } from '../actions/create-user.actions';
import { updateUser, updateUserFailure, updateUserSuccess } from '../actions/update-user.actions';
import * as fromSession from '@shared/reducers/session.reducer';
import { resetPassword, resetPasswordFailure, resetPasswordSuccess } from '../actions/reset-password.actions';
import {
  resendAccountActivation,
  resendAccountActivationFailure,
  resendAccountActivationSuccess,
} from '../actions/resend-account-activation.actions';
import {
  resendEmailConfirmation,
  resendEmailConfirmationFailure,
  resendEmailConfirmationSuccess,
} from '../actions/resend-email-confirmation.actions';
import { disableMfa, disableMfaFailure, disableMfaSuccess } from '@features/mfa/actions/disable-mfa.actions';
import { generateMfaRecoveryCodesSuccess } from '@features/mfa/actions/generate-mfa-recovery-codes.actions';
import { selectAddUserGroup } from '../actions/select-add-user-group.actions';
import { setSelectedUserId } from '../actions/set-selected-user-id.actions';
import { setUserFilters } from '../actions/set-user-filters.actions';
import {
  sendMfaRecoveryLink,
  sendMfaRecoveryLinkFailure,
  sendMfaRecoveryLinkSuccess,
} from '@features/mfa/actions/send-mfa-recovery-link.actions';
import { loadSessionSuccess } from '@shared/actions/load-session.actions';
import { Group } from '@shared/models/group';

export const featureKey = 'users';

export const usersAdapter = createEntityAdapter<User>({
  selectId: (user: User) => user.id,
});

export interface State extends EntityState<User> {
  paginationState: PaginationState;
  filters: UserFilters;
  sorting?: Sorting;
  loadingState: LoadingState;
  loadingText?: string;
  confirmEmailLoadingState: LoadingState;
  addUserGroup?: Partial<Group>;
  selectedUserId?: string;
}

export const initialState: State = usersAdapter.getInitialState({
  paginationState: defaultPaginationState,
  filters: {},
  loadingState: 'not_loading',
  loadingText: undefined,
  confirmEmailLoadingState: 'not_loading',
});

export const usersReducer = createReducer(
  initialState,
  on(loadUsers, createUser, updateUser, resetPassword, sendMfaRecoveryLink, resendAccountActivation, (state) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
  })),
  on(loadUser, (state, { userId }) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
    selectedUserId: userId,
  })),
  on(setSelectedUserId, (state, { userId }) => ({
    ...state,
    selectedUserId: userId,
  })),
  on(createUser, (state, { user }) => ({
    ...state,
    loadingText: `Creating ${user.attributes?.full_name}`,
  })),
  on(updateUser, (state) => ({
    ...state,
    loadingText: `Updating user...`,
  })),
  on(disableMfa, (state) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
    loadingText: `Disabling Mfa...`,
  })),
  on(resetPassword, (state) => ({
    ...state,
    loadingText: `Resetting password`,
  })),
  on(sendMfaRecoveryLink, (state) => ({
    ...state,
    loadingText: `Sending MFA recovery link`,
  })),
  on(resendAccountActivation, (state) => ({
    ...state,
    loadingText: `Resending account activation email`,
  })),
  on(setUserFilters, (state, { filters }) => ({
    ...state,
    filters: filters,
  })),
  on(loadUsersSuccess, (state, { data, paginationState, filters, sorting }): State => {
    return usersAdapter.setAll(data, {
      ...state,
      paginationState,
      loadingState: 'loaded' as LoadingState,
      filters: filters,
      sorting: sorting,
    });
  }),
  on(loadUserSuccess, createUserSuccess, updateUserSuccess, (state, { user }): State => {
    return usersAdapter.upsertOne(user, {
      ...state,
      loadingState: 'loaded' as LoadingState,
      selectedUserId: user.id,
    });
  }),
  on(loadSessionSuccess, (state, { session }): State => {
    if (session) {
      return usersAdapter.upsertOne({ id: session?.id!, attributes: session! }, state);
    } else {
      return state;
    }
  }),
  on(disableMfaSuccess, (state, { userId }): State => {
    let user = state.entities[userId];
    if (user) {
      return usersAdapter.updateOne(
        {
          id: userId,
          changes: {
            attributes: {
              ...user.attributes,
              mfa_setting: { ...user.attributes.mfa_setting, active: false },
            },
          },
        },
        { ...state, loadingState: 'loaded' },
      );
    } else {
      return state;
    }
  }),
  on(
    resetPasswordSuccess,
    sendMfaRecoveryLinkSuccess,
    resendAccountActivationSuccess,
    generateMfaRecoveryCodesSuccess,
    (state) => ({
      ...state,
      loadingState: 'loaded' as LoadingState,
    }),
  ),
  on(
    loadUsersFailure,
    loadUserFailure,
    createUserFailure,
    updateUserFailure,
    resetPasswordFailure,
    sendMfaRecoveryLinkFailure,
    resendAccountActivationFailure,
    disableMfaFailure,
    (state) => ({
      ...state,
      loadingState: 'not_loading' as LoadingState,
      loadingText: undefined,
    }),
  ),
  on(resendEmailConfirmation, (state, { fromBanner }) => {
    return {
      ...state,
      confirmEmailLoadingState: fromBanner ? 'loading' : state.confirmEmailLoadingState,
      loadingState: !fromBanner ? 'loading' : state.loadingState,
      loadingText: fromBanner ? `Resending email confirmation` : state.loadingText,
    };
  }),
  on(resendEmailConfirmationSuccess, (state, { fromBanner }) => ({
    ...state,
    confirmEmailLoadingState: fromBanner ? 'loaded' : state.confirmEmailLoadingState,
    loadingState: !fromBanner ? 'loaded' : state.loadingState,
  })),
  on(resendEmailConfirmationFailure, (state, { fromBanner }) => ({
    ...state,
    confirmEmailLoadingState: fromBanner ? 'not_loading' : state.confirmEmailLoadingState,
    loadingState: !fromBanner ? 'not_loading' : state.loadingState,
  })),
  on(selectAddUserGroup, (state, { group }) => ({
    ...state,
    addUserGroup: group,
  })),
);

export function reducer(state: State | undefined, action: Action) {
  return usersReducer(state, action);
}

export const usersFeatureSelector = createFeatureSelector<State>(featureKey);

const { selectIds, selectEntities, selectAll, selectTotal } = usersAdapter.getSelectors(usersFeatureSelector);

export const selectAllUsers = selectAll;

export const selectUsersLoadingState = createSelector(usersFeatureSelector, (state: State) => state.loadingState);

export const selectUsersLoadingText = createSelector(usersFeatureSelector, (state: State) => state.loadingText);

export const selectConfirmEmailLoading = createSelector(
  usersFeatureSelector,
  (state: State) => state.confirmEmailLoadingState,
);

export const selectUsersPaginationState = createSelector(usersFeatureSelector, (state: State) => state.paginationState);

export const selectUserById = (userId: string) =>
  createSelector(selectEntities, (entities) => (userId == undefined ? undefined : entities[userId]));

export const selectSelectedUser = createSelector(
  selectEntities,
  usersFeatureSelector,
  (entities, state) => entities[state.selectedUserId || ''],
);

export const selectOwnUser = createSelector(selectEntities, fromSession.selectSession, (entities, session) =>
  session == undefined ? undefined : entities[session.id || ''],
);

export const selectUserGroup = createSelector(usersFeatureSelector, (state: State) => {
  return state.addUserGroup;
});

export const selectUserFilters = createSelector(usersFeatureSelector, (state) => state.filters);
